import { QuestionsCollection } from '../../api/models/survey';
import {
    getOptionalAttendeesQuestions,
    getRequiredAttendeesQuestions,
} from '../../containers/checkout/utils';
import { getOptionalAndCannedAttendeesQuestions } from '../../containers/checkout/utils/typedUtils';
import { OrderAttendee } from '../../models/attendee';
import {
    AttendeeSurvey,
    isCustomSurveyQuestion,
    shouldIncludeInAttendeeQuestions,
} from '../../models/survey';
import { WithOrderNamespace } from '../../state/order';
import { WithSurveyNamespace } from '../../state/survey';
import { createSelector, getNamespaceValue } from '../utils/selector';
import { buildAttendeeSurveys } from './buildAttendeeSurvey';
import { getQuestionsCollection } from './pureSelectors';

export const getQuestionsForAttendees = createSelector<
    Array<AttendeeSurvey>,
    (
        attendees: Array<OrderAttendee>,
        questionsCollection: QuestionsCollection,
    ) => Array<AttendeeSurvey>
>(
    (state: WithOrderNamespace) => getNamespaceValue(state.order, 'attendees'),
    (state: WithSurveyNamespace) => getQuestionsCollection(state),
    (attendees, questionsCollection) =>
        buildAttendeeSurveys(
            attendees,
            questionsCollection,
            shouldIncludeInAttendeeQuestions,
        ),
);

export const getOptionalQuestionsForAttendees = createSelector<
    ReturnType<typeof getOptionalAttendeesQuestions>,
    (
        attendees: Array<OrderAttendee>,
        questionsCollection: QuestionsCollection,
    ) => ReturnType<typeof getOptionalAttendeesQuestions>
>(
    (state: WithOrderNamespace) => getNamespaceValue(state.order, 'attendees'),
    (state: WithSurveyNamespace) => getQuestionsCollection(state),
    (
        attendees: Array<OrderAttendee>,
        questionsCollection: QuestionsCollection,
    ) => {
        const attendeeSurvey = buildAttendeeSurveys(
            attendees,
            questionsCollection,
            shouldIncludeInAttendeeQuestions,
        );
        return getOptionalAttendeesQuestions(attendeeSurvey);
    },
);

export const getCannedAndOptionalQuestionsForAttendees = createSelector<
    ReturnType<typeof getOptionalAndCannedAttendeesQuestions>,
    (
        attendees: Array<OrderAttendee>,
        questionsCollection: QuestionsCollection,
    ) => ReturnType<typeof getOptionalAndCannedAttendeesQuestions>
>(
    (state: WithOrderNamespace) => getNamespaceValue(state.order, 'attendees'),
    (state: WithSurveyNamespace) => getQuestionsCollection(state),
    (attendees, questionsCollection) => {
        const attendeeSurvey = buildAttendeeSurveys(
            attendees,
            questionsCollection,
            shouldIncludeInAttendeeQuestions,
        );
        return getOptionalAndCannedAttendeesQuestions(attendeeSurvey);
    },
);

export const getRequiredCustomQuestionsForAttendees = createSelector<
    ReturnType<typeof getRequiredAttendeesQuestions>,
    (
        attendees: Array<OrderAttendee>,
        questionsCollection: QuestionsCollection,
    ) => ReturnType<typeof getRequiredAttendeesQuestions>
>(
    (state: WithOrderNamespace) => getNamespaceValue(state.order, 'attendees'),
    (state: WithSurveyNamespace) => getQuestionsCollection(state),
    (attendees, questionsCollection) => {
        const attendeeSurvey = buildAttendeeSurveys(
            attendees,
            questionsCollection,
            isCustomSurveyQuestion,
        );
        return getRequiredAttendeesQuestions(attendeeSurvey);
    },
);

export const getRequiredQuestionsForAttendees = createSelector<
    ReturnType<typeof getRequiredAttendeesQuestions>,
    (
        attendees: Array<OrderAttendee>,
        questionsCollection: QuestionsCollection,
    ) => ReturnType<typeof getRequiredAttendeesQuestions>
>(
    (state: WithOrderNamespace) => getNamespaceValue(state.order, 'attendees'),
    (state: WithSurveyNamespace) => getQuestionsCollection(state),
    (attendees, questionsCollection) => {
        const attendeeSurvey = buildAttendeeSurveys(
            attendees,
            questionsCollection,
            shouldIncludeInAttendeeQuestions,
        );
        return getRequiredAttendeesQuestions(attendeeSurvey);
    },
);
